import { Add as AddIcon } from '@mui/icons-material';
import { Box, Grid, Stack, Typography } from '@mui/material';
import React from 'react';
import { ChoiceGlyph, ChoiceGlyphCorner, ChoiceTile, ChoiceTileCorner } from './ChoiceTile.styles';
import { CircleSwatch } from './CircleSwatch.styles';
import { SelectionOutline } from 'src/UIKit';
import { QRCodeStylingOptions } from '../../../types';

interface QRCodeAppearanceOptionsProps {
  styling: QRCodeStylingOptions;
  colorPalette: string[];
  lastCustomColor: string;
  onLastCustomColorChange: (color: string) => void;
  onStylingChange: (field: keyof QRCodeStylingOptions, value: string | boolean | number) => void;
}

interface ShapeChoice<T extends string> {
  value: T;
  corner: ChoiceTileCorner;
  glyph: ChoiceGlyphCorner;
}

const SHAPES: ShapeChoice<NonNullable<QRCodeStylingOptions['shape']>>[] = [
  { value: 'square', corner: 'large', glyph: 'small' },
  { value: 'circle', corner: 'round', glyph: 'round' },
];

const DOT_TYPES: ShapeChoice<NonNullable<QRCodeStylingOptions['dotType']>>[] = [
  { value: 'square', corner: 'small', glyph: 'none' },
  { value: 'rounded', corner: 'small', glyph: 'small' },
  { value: 'circle', corner: 'small', glyph: 'round' },
];

const isWhite = (color: string) => color === '#FFFFFF' || color === '#ffffff';

export const QRCodeAppearanceOptions = ({
  styling,
  colorPalette,
  lastCustomColor,
  onLastCustomColorChange,
  onStylingChange,
}: QRCodeAppearanceOptionsProps) => (
  <>
    {/* Color Selection */}
    <Typography variant="subtitle2" mb={1} fontWeight="fontWeightSemiBold">
      Color
    </Typography>
    <Box mb={3}>
      <Grid container spacing={1}>
        {colorPalette.map(color => (
          <Grid item key={color}>
            <SelectionOutline selected={styling.foregroundColor === color}>
              <CircleSwatch
                color={color}
                bordered={color === '#FFFFFF'}
                onClick={() => onStylingChange('foregroundColor', color)}
              />
            </SelectionOutline>
          </Grid>
        ))}
        <Grid item>
          <Box sx={{ position: 'relative', width: 32, height: 32 }}>
            <input
              type="color"
              value={lastCustomColor}
              onClick={() => {
                if (!isWhite(lastCustomColor)) {
                  onStylingChange('foregroundColor', lastCustomColor);
                }
              }}
              onChange={e => {
                onLastCustomColorChange(e.target.value);
                onStylingChange('foregroundColor', e.target.value);
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                zIndex: 2,
              }}
            />
            <SelectionOutline
              selected={
                !colorPalette.includes(styling.foregroundColor.toUpperCase())
                && styling.foregroundColor === lastCustomColor
              }
            >
              <CircleSwatch
                color={lastCustomColor}
                bordered={isWhite(lastCustomColor)}
                sx={{ pointerEvents: 'none' }}
              >
                <AddIcon fontSize="small" />
              </CircleSwatch>
            </SelectionOutline>
          </Box>
        </Grid>
      </Grid>
    </Box>
    {/* QR Code Shape and Dot Type */}
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 3 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" mb={1} fontWeight="fontWeightSemiBold">
          Shape
        </Typography>
        <Stack direction="row" spacing={2}>
          {SHAPES.map(shape => (
            <ChoiceTile
              key={shape.value}
              selected={styling.shape === shape.value}
              corner={shape.corner}
              onClick={() => onStylingChange('shape', shape.value)}
            >
              <ChoiceGlyph corner={shape.glyph} />
            </ChoiceTile>
          ))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" mb={1} fontWeight="fontWeightSemiBold">
          Dot type
        </Typography>
        <Stack direction="row" spacing={2}>
          {DOT_TYPES.map(dotType => (
            <ChoiceTile
              key={dotType.value}
              selected={styling.dotType === dotType.value}
              corner={dotType.corner}
              onClick={() => onStylingChange('dotType', dotType.value)}
            >
              <ChoiceGlyph corner={dotType.glyph} />
            </ChoiceTile>
          ))}
        </Stack>
      </Box>
    </Box>
  </>
);
